import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { STATUSES, OFFERS } from "@/data/mockData";
import { agentsApi, contactsApi } from "@/lib/api";
import { PageHeader, StatusBadge, ScoreBadge, OfferBadge, EditorialButton } from "@/lib/editorial";
import { ChevronLeft, Plus } from "lucide-react";
import { toast } from "sonner";

export default function AgentContacts() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [offerFilter, setOfferFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [urlsDraft, setUrlsDraft] = useState("");
  const [addOfferId, setAddOfferId] = useState("");
  const [addCampaignType, setAddCampaignType] = useState("");

  const { data: agent } = useQuery({ queryKey: ["agent", id], queryFn: () => agentsApi.get(id), enabled: !!id });
  const { data, isLoading } = useQuery({
    queryKey: ["contacts", id, page, statusFilter, offerFilter],
    queryFn: () => contactsApi.list(id, {
      page,
      statusFilter: statusFilter === "ALL" ? "" : statusFilter,
      offerFilter: offerFilter === "ALL" ? "" : offerFilter,
    }),
    enabled: !!id,
    keepPreviousData: true,
  });

  const addMut = useMutation({
    mutationFn: () => {
      const urls = urlsDraft.split("\n").map((s) => s.trim()).filter(Boolean);
      return contactsApi.manualAdd(id, urls, addOfferId ? Number(addOfferId) : undefined, addCampaignType || undefined);
    },
    onSuccess: (res) => {
      toast(`Added ${res.added}${res.duplicates ? `, ${res.duplicates} already known` : ""}${res.invalid ? `, ${res.invalid} not a linkedin.com/in/ URL` : ""}.`);
      setUrlsDraft(""); setShowAdd(false);
      qc.invalidateQueries({ queryKey: ["contacts", id] });
    },
    onError: (e) => toast.error(e.message || "Add failed."),
  });

  const contacts = data?.contacts ?? [];
  const pageCount = data?.pages ?? 1;
  const total = data?.total ?? 0;

  if (!agent && !isLoading) return null;

  return (
    <div className="px-8 lg:px-14 py-12">
      <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-6">
        <Link to="/admin/agents" className="hover:text-[var(--ink)] flex items-center gap-1">
          <ChevronLeft className="w-3.5 h-3.5" /> Agents
        </Link>
        <span>/</span>
        <Link to={`/admin/agents/${id}`} className="hover:text-[var(--ink)]">{agent?.name}</Link>
        <span>/</span>
        <span className="text-[var(--ink)]">Contacts</span>
      </div>

      <PageHeader
        eyebrow={`Agent #${id} · contacts`}
        tape="Pipeline"
        rest="of prospects"
        sub={`Every contact tied to ${agent?.name ?? "…"}. Filter by status or offer.`}
        testId="contacts-title"
      />

      <div className="mb-6">
        <EditorialButton variant="secondary" onClick={() => setShowAdd((v) => !v)} data-testid="contacts-add-toggle">
          <Plus className="w-3.5 h-3.5" /> Add contacts manually
        </EditorialButton>
        {showAdd && (
          <div className="mt-4 border border-[var(--rule-light)] bg-[var(--paper-2)] p-5 max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--muted-light)] mb-2">
              LinkedIn profile URLs, one per line
            </div>
            <textarea
              value={urlsDraft}
              onChange={(e) => setUrlsDraft(e.target.value)}
              rows={5}
              placeholder="https://www.linkedin.com/in/someone/"
              data-testid="contacts-add-urls"
              className="w-full bg-white border border-[var(--rule-light)] focus:border-[var(--blue)] outline-none p-3 font-mono text-[12px] resize-none"
            />
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <select
                value={addOfferId}
                onChange={(e) => { setAddOfferId(e.target.value); if (e.target.value) setAddCampaignType(""); }}
                className="border border-[var(--rule-light)] bg-white text-[12px] px-2 py-1.5"
              >
                <option value="">No offer</option>
                {OFFERS.map((o) => <option key={o.id} value={o.id}>#{o.id} {o.name}</option>)}
              </select>
              <input
                value={addCampaignType}
                onChange={(e) => { setAddCampaignType(e.target.value); if (e.target.value) setAddOfferId(""); }}
                placeholder="or campaign type (e.g. coffee_date)"
                className="border border-[var(--rule-light)] bg-white text-[12px] px-2 py-1.5 font-mono"
              />
              <EditorialButton
                onClick={() => addMut.mutate()}
                disabled={!urlsDraft.trim() || addMut.isPending}
                data-testid="contacts-add-submit"
              >
                {addMut.isPending ? "Adding…" : "Add"}
              </EditorialButton>
            </div>
          </div>
        )}
      </div>

      {/* Status filter chips */}
      <div className="border-y border-[var(--rule-light)] py-4 mb-2 flex items-center gap-3 flex-wrap">
        <span className="text-[10px] uppercase tracking-[0.24em] text-[var(--muted-light)] mr-2">By status</span>
        <FilterChip active={statusFilter === "ALL"} onClick={() => { setStatusFilter("ALL"); setPage(1); }} testId="status-all">All</FilterChip>
        {STATUSES.map((s) => (
          <FilterChip key={s} active={statusFilter === s} onClick={() => { setStatusFilter(s); setPage(1); }} testId={`status-${s}`}>
            {s}
          </FilterChip>
        ))}
      </div>

      {/* Offer filter chips */}
      <div className="border-b border-[var(--rule-light)] py-4 mb-6 flex items-center gap-3 flex-wrap">
        <span className="text-[10px] uppercase tracking-[0.24em] text-[var(--muted-light)] mr-2">By offer</span>
        <FilterChip active={offerFilter === "ALL"} onClick={() => { setOfferFilter("ALL"); setPage(1); }} testId="offer-all">All</FilterChip>
        {OFFERS.map((o) => (
          <FilterChip key={o.id} active={offerFilter === String(o.id)} onClick={() => { setOfferFilter(String(o.id)); setPage(1); }} testId={`offer-${o.id}`}>
            #{o.id}
          </FilterChip>
        ))}
      </div>

      <div className="text-[11px] text-[var(--muted-light)] mb-4 font-mono">
        {total.toLocaleString()} contacts · page {page} of {pageCount}
      </div>

      <table className="w-full text-[13px]" data-testid="contacts-table">
        <thead>
          <tr className="text-left border-b border-[var(--rule-light)]">
            <Th>Name</Th>
            <Th>Headline</Th>
            <Th>Company</Th>
            <Th>Status</Th>
            <Th>Offer</Th>
            <Th>Score</Th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr><td colSpan={6} className="py-8 text-center text-[var(--muted-light)]">Loading…</td></tr>
          ) : contacts.length === 0 ? (
            <tr><td colSpan={6} className="py-8 text-center text-[var(--muted-light)]">No contacts match the current filter.</td></tr>
          ) : contacts.map((c) => (
            <tr key={c.id} className="border-b border-[var(--rule-light)] hover:bg-[var(--paper-2)]">
              <td className="py-3 pr-4 font-display text-[15px]">{c.name}</td>
              <td className="py-3 pr-4 truncate max-w-[260px] text-[var(--muted-light)]">{c.headline}</td>
              <td className="py-3 pr-4 truncate max-w-[160px] text-[var(--muted-light)]">{c.company}</td>
              <td className="py-3 pr-4"><StatusBadge status={c.status} /></td>
              <td className="py-3 pr-4"><OfferBadge offerId={c.offer} /></td>
              <td className="py-3 pr-4"><ScoreBadge score={c.score} /></td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="mt-6 flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 border border-[var(--rule-light)] text-[12px] disabled:opacity-30">← Prev</button>
          {Array.from({ length: Math.min(pageCount, 7) }, (_, i) => i + 1).map((p) => (
            <button key={p} onClick={() => setPage(p)} className={`px-3 py-1.5 border text-[12px] ${page === p ? "border-[var(--blue)] text-[var(--blue)]" : "border-[var(--rule-light)]"}`}>{p}</button>
          ))}
          <button disabled={page >= pageCount} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 border border-[var(--rule-light)] text-[12px] disabled:opacity-30">Next →</button>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1 text-[11px] uppercase tracking-[0.16em] border transition-colors ${
        active ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--rule-light)] hover:border-[var(--ink)]/40"
      }`}
    >
      {children}
    </button>
  );
}

function Th({ children }) {
  return <th className="text-left text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] py-3 pr-4">{children}</th>;
}
